import MapPage from "./pages/Map";
import React, { useState, useCallback, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  NavLink,
  Outlet,
  Navigate,
  useLocation
} from "react-router-dom";

import Dashboard from "./pages/Dashboard";
import Machines from "./pages/Machines";
import Inventory from "./pages/Inventory";
import SlotsConfig from "./pages/SlotsConfig";
import Sales from "./pages/Sales";
import Login from "./pages/Login";
import Devices from "./pages/Devices";
import Qibixx from "./pages/Qibixx";
import Fleet from "./pages/Fleet";
import OperationsControl from "./pages/OperationsControl";
import SupportCustomer from "./pages/SupportCustomer";
import SupportAdmin from "./pages/SupportAdmin";
import ProtectedRoute from "./components/ProtectedRoute";

const navSections = [
  {
    title: "Principal",
    items: [{ to: "/", end: true, label: "Dashboard" }]
  },
  {
    title: "Operación",
    items: [
      { to: "/control", label: "Control" },
      { to: "/fleet", label: "Flota" },
      { to: "/machines", label: "Máquinas" },
      { to: "/inventory", label: "Inventario" },
      { to: "/slots", label: "Slots" },
      { to: "/sales", label: "Ventas" },
      { to: "/devices", label: "Dispositivos" },
      { to: "/qibixx", label: "Qibixx" },
      { to: "/map", label: "Mapa" }
    ]
  },
  {
    title: "Atención al cliente",
    items: [{ to: "/soporte-panel", label: "Soporte QR" }]
  }
];

const navItems = navSections.flatMap(s => s.items);

function currentPageTitle(pathname) {
  const norm = pathname.replace(/\/$/, "") || "/";
  if (norm === "/") return "Dashboard";
  const hit = navItems.find(
    n => !n.end && (norm === n.to || norm.startsWith(`${n.to}/`))
  );
  if (hit) return hit.label;
  if (norm.startsWith("/soporte-panel")) return "Soporte QR";
  return "SVC Vikingo";
}

function AppShell({ onLogout }) {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (menuOpen) document.body.classList.add("nav-drawer-open");
    else document.body.classList.remove("nav-drawer-open");
    return () => document.body.classList.remove("nav-drawer-open");
  }, [menuOpen]);

  return (
    <div className="app-shell">
      <header className="mobile-topbar" aria-label="Barra móvil">
        <button
          type="button"
          className="mobile-topbar__menu"
          onClick={() => setMenuOpen(o => !o)}
          aria-expanded={menuOpen}
          aria-controls="sidebar-nav-main"
          aria-label={menuOpen ? "Cerrar menú" : "Abrir menú de navegación"}
        >
          <span className="mobile-topbar__burger" aria-hidden />
        </button>
        <span className="mobile-topbar__title">{currentPageTitle(pathname)}</span>
      </header>

      <div
        className={`sidebar-backdrop${menuOpen ? " sidebar-backdrop--visible" : ""}`}
        onClick={closeMenu}
        aria-hidden={!menuOpen}
      />

      <aside className={`sidebar${menuOpen ? " sidebar--open" : ""}`} id="app-sidebar">
        <div className="sidebar-brand">
          <h2 className="sidebar-brand__title">SVC Vikingo</h2>
          <p className="sidebar-brand__subtitle">Panel operativo</p>
        </div>
        <nav className="sidebar-nav" id="sidebar-nav-main">
          {navSections.map(section => (
            <div key={section.title} className="sidebar-nav__group">
              <p className="sidebar-nav__heading">{section.title}</p>
              {section.items.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `sidebar-link${isActive ? " sidebar-link--active" : ""}`
                  }
                  onClick={closeMenu}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <button type="button" className="btn btn-ghost" onClick={onLogout}>
            Cerrar sesión
          </button>
        </div>
      </aside>

      <main className="main-content">
        <div className="main-content__inner">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function App() {
  const [auth, setAuth] = useState(() => !!localStorage.getItem("token"));

  const logout = () => {
    localStorage.removeItem("token");
    setAuth(false);
  };

  return (
    <Router>
      <Routes>
        <Route path="/soporte/:machineId" element={<SupportCustomer />} />
        <Route
          path="/login"
          element={
            auth || localStorage.getItem("token") ? (
              <Navigate to="/" replace />
            ) : (
              <Login onLogin={() => setAuth(true)} />
            )
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppShell onLogout={logout} />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="control" element={<OperationsControl />} />
          <Route path="fleet" element={<Fleet />} />
          <Route path="machines" element={<Machines />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="slots" element={<SlotsConfig />} />
          <Route path="sales" element={<Sales />} />
          <Route path="devices" element={<Devices />} />
          <Route path="qibixx" element={<Qibixx />} />
          <Route path="map" element={<MapPage />} />
          <Route path="soporte-panel" element={<SupportAdmin />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
